import { wrapCreateBrowserRouterV7 } from '@sentry/react';
import { createBrowserRouter, Outlet, RouteObject } from 'react-router';
import AiChatSellingGuard from '~/guards/AiChatSellingGuard';
import { AuthGuard } from '~/guards/AuthGuard';
import { CompanyGuard } from '~/guards/CompanyGuard';
import ExcludeSwanOnlyGuard from '~/guards/ExcludeSwanOnlyGuard';
import LeadSourceGuard from '~/guards/LeadSourcesGuard';
import { PublicGuard } from '~/guards/PublicGuard';
import UserFeaturesGuard from '~/guards/UserFeaturesGuard';
import DashboardLayout from '~/layouts/dashboard';
import DashboardRedirects from '~/layouts/dashboard/DashboardRedirects';
import { FeatureFlag, LeadSource, UserRole } from '~/types/company';
import lazyImport from '~/utils/lazyImport';
import ActionConsentCallbackRedirect from '~/views/ActionConsentCallbackRedirect';
import { f24Routes } from '~/views/F24/routing/f24-routes';
import { accountsRoutes } from '~/views/accounts/accounts-routes';
import { analyticsRoutes } from '~/views/analytics/routing/analytics-routes';
import { bookkeepingRoutes } from '~/views/bookkeeping/bookkeeping-routes';
import { cashflowRoutes } from '~/views/cash/routing/cashflow-routes';
import { chatRoutes } from '~/views/chat/routing/chat-routes';
import { invoicesRoutes } from '~/views/invoices/routing/invoices-routes';
import { outstandingRoutes } from '~/views/outstanding/routing/outstanding-routes';
import { referralRoutes } from '~/views/referral/routing/referral-routes';
import { settingsRoutes } from '~/views/settings/routing/settings-routes';
import { transactionsRoutes } from '~/views/transactions/routing/transactions-routes';
import AppRoot from './AppRoot';
import RedirectWithQueryParams from './RedirectWithQueryParams';
import { RouteConfig } from './routeConfig';

const AcceptInvite = lazyImport(() => import('~/views/auth/accept-invite/AcceptInvite'));
const DownloadApp = lazyImport(() => import('~/views/DownloadApp'));
const Page404 = lazyImport(() => import('../views/error/Page404'));
const PaymentsPdfPage = lazyImport(() => import('~/views/payments/pdf/PaymentsPdfPage'));
const PaymentsBulkPage = lazyImport(() => import('~/views/payments/bulk/BulkPaymentPage'));
const PaymentsBulkDraftRedirectPage = lazyImport(() => import('~/views/payments/bulk/BulkPaymentDraftRedirectPage'));
const PaymentsBulkPdfPage = lazyImport(() => import('~/views/payments/bulk-pdf/PaymentsBulkPdfPage'));
const PaymentsRedirectPage = lazyImport(() => import('~/views/paymentsRedirect/PaymentsRedirectPage'));
const Login = lazyImport(() => import('../views/auth/Login'));
const PasskeySetupPage = lazyImport(() => import('../views/auth/PasskeySetupPage'));
const TermsAcceptancePage = lazyImport(() => import('../views/auth/TermsAcceptancePage'));
const LogoutPage = lazyImport(() => import('../views/auth/LogoutPage'));
const SignupUserPage = lazyImport(() => import('../views/auth/SignupUserPage'));
const SignupEmailVerificationPage = lazyImport(() => import('../views/auth/SignupEmailVerificationPage'));
const SignupAddCompanyPage = lazyImport(() => import('../views/auth/SignupAddCompanyPage'));
const SignupPlanSummaryPage = lazyImport(() => import('../views/auth/SignupPlanSummaryPage'));
const SignupJoinSummaryPage = lazyImport(() => import('../views/auth/SignupJoinSummaryPage'));
const SignupWelcomePage = lazyImport(() => import('../views/auth/SignupWelcomePage'));
const ChangePassword = lazyImport(() => import('../views/auth/ChangePassword'));
const ResetPassword = lazyImport(() => import('../views/auth/ResetPassword'));
const DownloadBookkeepingLedger = lazyImport(() => import('../views/bookkeeping/ledgers/BookkeepingLedgerDownload'));
const SharedBookkeepingLedger = lazyImport(() => import('../views/bookkeeping/ledgers/SharedBookkeepingLedger'));
const SharedInvoice = lazyImport(() => import('../views/invoices/SharedInvoice'));
const SharedProvisionalBalance = lazyImport(
  () => import('../views/bookkeeping/provisional-balance/SharedProvisionalBalance'),
);
const DownloadProvisionalBalance = lazyImport(
  () => import('../views/bookkeeping/provisional-balance/download/DownloadProvisionalBalance'),
);
const F24PreviewPage = lazyImport(() => import('../views/bookkeeping/taxes/F24ListTable/F24PreviewPage'));
const IntrastatPreviewPage = lazyImport(() => import('../views/bookkeeping/intrastat/IntrastatPreviewPage'));
const UnauthorisedCompanyPage = lazyImport(() => import('../views/unauthorisedCompany/UnauthorisedCompanyPage'));
const ExpiredSubscriptionPage = lazyImport(() => import('../views/subscriptions/ExpiredSubscriptionPage'));

const AccountsSection = lazyImport(() => import('../views/accounts/AccountsSection'));
const TransactionsSection = lazyImport(() => import('../views/transactions/TransactionsSection'));
const ReconciliationsSection = lazyImport(() => import('../views/reconciliations/ReconciliationsSection'));
const CashSection = lazyImport(() => import('../views/cash/CashSection'));
const OutstandingSection = lazyImport(() => import('../views/outstanding/OutstandingSection'));
const InvoicesSection = lazyImport(() => import('../views/invoices/InvoicesSection'));
const CounterpartsSection = lazyImport(() => import('../views/counterparts/CounterpartsSection'));
const SettingsSection = lazyImport(() => import('../views/settings/SettingsSection'));
const ConsentsRedirect = lazyImport(() => import('../views/consents/ConsentsRedirect'));
const CompanyRedirect = lazyImport(() => import('../views/CompanyRedirect'));
const ErrorView = lazyImport(() => import('../views/error/Error'));
const ReferralSection = lazyImport(() => import('~/views/referral/ReferralSection'));
const routes: RouteObject[] = [
  {
    path: '*',
    element: <AppRoot />,
    children: [
      {
        path: RouteConfig.NotFound.template,
        element: <Page404 />,
      },
      {
        path: RouteConfig.Logout.template,
        element: <LogoutPage />,
      },
      {
        path: RouteConfig.Login.template,
        element: (
          <PublicGuard>
            <Login />
          </PublicGuard>
        ),
      },
      {
        path: RouteConfig.PasskeySetup.template,
        element: (
          <AuthGuard>
            <PasskeySetupPage />
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.TermsAcceptance.template,
        element: (
          <AuthGuard>
            <TermsAcceptancePage />
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.DownloadApp.template,
        element: <DownloadApp />,
      },
      {
        path: RouteConfig.SignUpEmailVerification.template,
        element: <SignupEmailVerificationPage />,
      },
      {
        path: RouteConfig.SignUpUser.template,
        element: <SignupUserPage />,
      },
      {
        path: RouteConfig.CompanyRedirect.template,
        element: (
          <AuthGuard>
            <CompanyRedirect />
          </AuthGuard>
        ),
      },

      {
        path: RouteConfig.SignUpAddCompany.template,
        element: (
          <AuthGuard>
            <SignupAddCompanyPage />
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SignUpPlanSummary.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <SignupPlanSummaryPage />
            </CompanyGuard>
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SignUpJoinSummary.template,
        element: (
          <AuthGuard>
            <SignupJoinSummaryPage />
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SignUpWelcome.template,
        element: (
          <AuthGuard>
            <SignupWelcomePage />
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SignUp.template,
        element: <RedirectWithQueryParams path={RouteConfig.SignUpEmailVerification.buildLink()} />,
      },
      {
        path: RouteConfig.AcceptInvite.template,
        element: <AcceptInvite />,
      },
      {
        path: RouteConfig.ChangePassword.template,
        element: (
          <PublicGuard>
            <ChangePassword />
          </PublicGuard>
        ),
      },
      {
        path: RouteConfig.ResetPassword.template,
        element: (
          <PublicGuard>
            <ResetPassword />
          </PublicGuard>
        ),
      },
      {
        path: RouteConfig.SharedBookkeepingLedger.template,
        element: <SharedBookkeepingLedger />,
      },
      {
        path: RouteConfig.DownloadBookkeepingLedger.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DownloadBookkeepingLedger />
            </CompanyGuard>
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SharedInvoice.template,
        element: <SharedInvoice />,
      },
      {
        path: RouteConfig.DownloadProvisionalBalance.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DownloadProvisionalBalance />
            </CompanyGuard>
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.BookkeepingF24Preview.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <F24PreviewPage />
            </CompanyGuard>
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.BookkeepingIntrastatPreview.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <IntrastatPreviewPage />
            </CompanyGuard>
          </AuthGuard>
        ),
      },
      {
        path: RouteConfig.SharedProvisionalBalance.template,
        element: <SharedProvisionalBalance />,
      },
      {
        path: RouteConfig.UnauthorisedCompany.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DashboardLayout isCompanyAccessible={false} />
            </CompanyGuard>
          </AuthGuard>
        ),
        children: [{ path: '*', element: <UnauthorisedCompanyPage /> }],
      },
      {
        path: RouteConfig.ExpiredSubscription.template,
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DashboardLayout isCompanyAccessible={false} />
            </CompanyGuard>
          </AuthGuard>
        ),
        children: [{ path: '*', element: <ExpiredSubscriptionPage /> }],
      },
      {
        path: '*',
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DashboardLayout />
            </CompanyGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <DashboardRedirects /> },
          {
            path: RouteConfig.Accounts.template,
            element: <AccountsSection />,
            children: accountsRoutes,
          },
          {
            path: RouteConfig.Transactions.template,
            element: (
              <UserFeaturesGuard requiredFeatures={[FeatureFlag.Treasury]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
                <TransactionsSection />
              </UserFeaturesGuard>
            ),
            children: transactionsRoutes,
          },
          {
            path: RouteConfig.Reconciliations.template,
            element: (
              <LeadSourceGuard forbiddenLeadSources={[LeadSource.BANK_ACCOUNT]}>
                <ExcludeSwanOnlyGuard>
                  <UserFeaturesGuard
                    requiredFeatures={[FeatureFlag.AutomaticReconciliation]}
                    whitelistedRoles={[UserRole.ENTREPRENEUR]}
                  >
                    <ReconciliationsSection />
                  </UserFeaturesGuard>
                </ExcludeSwanOnlyGuard>
              </LeadSourceGuard>
            ),
          },

          {
            path: RouteConfig.Cashflow.template,
            element: (
              <LeadSourceGuard forbiddenLeadSources={[LeadSource.BANK_ACCOUNT]}>
                <UserFeaturesGuard requiredFeatures={[FeatureFlag.Cashflow]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
                  <CashSection />
                </UserFeaturesGuard>
              </LeadSourceGuard>
            ),
            children: cashflowRoutes,
          },
          {
            path: RouteConfig.Outstanding.template,
            element: (
              <LeadSourceGuard forbiddenLeadSources={[LeadSource.BANK_ACCOUNT]}>
                <UserFeaturesGuard
                  requiredFeatures={[FeatureFlag.Outstanding]}
                  whitelistedRoles={[UserRole.ENTREPRENEUR]}
                >
                  <OutstandingSection />
                </UserFeaturesGuard>
              </LeadSourceGuard>
            ),
            children: outstandingRoutes,
          },
          {
            path: RouteConfig.Invoices.template,
            element: (
              <LeadSourceGuard forbiddenLeadSources={[LeadSource.BANK_ACCOUNT]}>
                <UserFeaturesGuard
                  requiredFeatures={[FeatureFlag.SeeInvoices]}
                  whitelistedRoles={[UserRole.ENTREPRENEUR]}
                >
                  <InvoicesSection />
                </UserFeaturesGuard>
              </LeadSourceGuard>
            ),
            children: invoicesRoutes,
          },
          {
            path: RouteConfig.Counterparts.template,
            element: (
              <UserFeaturesGuard requiredFeatures={[FeatureFlag.SeeInvoices]}>
                <CounterpartsSection />
              </UserFeaturesGuard>
            ),
          },
          {
            path: RouteConfig.Settings.template,
            element: <SettingsSection />,
            children: settingsRoutes,
          },
          { path: RouteConfig.Error.template, element: <ErrorView /> },
          { path: RouteConfig.ConsentsRedirect.template, element: <ConsentsRedirect /> },
          {
            path: RouteConfig.PaymentsRedirect.template,
            element: <PaymentsRedirectPage />,
          },
          {
            path: RouteConfig.PaymentsPdf.template,
            element: <PaymentsPdfPage />,
          },
          {
            path: RouteConfig.PaymentsBulk.template,
            element: <PaymentsBulkPage />,
          },
          {
            path: RouteConfig.PaymentsBulkDraft.template,
            element: <PaymentsBulkDraftRedirectPage />,
          },
          {
            path: RouteConfig.PaymentsBulkPdf.template,
            element: <PaymentsBulkPdfPage />,
          },
          {
            path: RouteConfig.Referral.template,
            element: <ReferralSection />,
            children: referralRoutes,
          },
          {
            path: RouteConfig.Analytics.template,
            children: analyticsRoutes,
          },
          {
            path: RouteConfig.Bookkeeping.template,
            children: bookkeepingRoutes,
          },
          {
            path: RouteConfig.Chat.template,
            element: (
              <AiChatSellingGuard>
                <Outlet />
              </AiChatSellingGuard>
            ),
            children: chatRoutes,
          },
          {
            path: RouteConfig.F24.template,
            element: (
              <UserFeaturesGuard requiredFeatures={[FeatureFlag.F24Management]}>
                <Outlet />
              </UserFeaturesGuard>
            ),
            children: f24Routes,
          },
          {
            path: RouteConfig.ActionConsentCallback.template,
            element: <ActionConsentCallbackRedirect />,
          },
        ],
      },
      {
        path: '*',
        element: (
          <AuthGuard>
            <CompanyGuard>
              <DashboardLayout isCompanyAccessible={false} />
            </CompanyGuard>
          </AuthGuard>
        ),
        children: [
          {
            path: '*',
            element: <Page404 />,
          },
        ],
      },
    ],
  },
];

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter(routes);

export default router;
